import { supabase } from './auth';
import { getAccessibleClientIds } from './multiClientUtils';
import { getCurrentUser } from './auth';

export async function getAppointments(clientIds?: string | string[], options?: {
  limit?: number;
  offset?: number;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc'
}) {
  let query = supabase
    .from('appointments')
    .select(`
      *,
      clients (
        id,
        name
      ),
      appointments_edenred (
        edenred_status,
        edenred_sent_at,
        edenred_lead_id
      )
    `)

  // Appliquer l'ordre spécifié ou l'ordre par défaut
  const orderBy = options?.orderBy || 'booking_date';
  const ascending = options?.orderDirection === 'asc';

  query = query.order(orderBy, { ascending });

  if (clientIds) {
    if (Array.isArray(clientIds)) {
      if (clientIds.length > 0) {
        query = query.in('client_id', clientIds);
      } else {
        return [];
      }
    } else {
      query = query.eq('client_id', clientIds);
    }
  }

  if (options?.limit) {
    query.limit(options.limit);
  }

  if (options?.offset) {
    query = query.range(options.offset, options.offset + (options.limit || 20) - 1);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data;
}

export async function getClients(options?: { limit?: number; offset?: number }) {
  const profile = await getCurrentUser();

  if (!profile) {
    throw new Error('User not authenticated');
  }

  let query = supabase
    .from('clients')
    .select('*')
    .order('statut', { ascending: true })
    .order('created_at', { ascending: false });

  if (profile.role === 'client') {
    const { data: profileClients, error: pcError } = await supabase
      .from('profile_clients')
      .select('client_id')
      .eq('profile_id', profile.user_id);

    if (pcError) throw pcError;

    const clientIds = profileClients?.map(pc => pc.client_id) || [];

    if (clientIds.length === 0) {
      return [];
    }

    query = query.in('id', clientIds);
  }

  if (options?.limit) {
    query.limit(options.limit);
  }

  if (options?.offset) {
    query = query.range(options.offset, options.offset + (options.limit || 20) - 1);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data;
}

export async function getClientById(id: string) {
  const profile = await getCurrentUser();

  if (!profile) {
    throw new Error('User not authenticated');
  }

  const { data, error } = await supabase
    .from('clients')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) throw error;
  if (!data) throw new Error('Client not found');

  if (profile.role === 'client') {
    const { data: hasAccess } = await supabase
      .from('profile_clients')
      .select('client_id')
      .eq('profile_id', profile.user_id)
      .eq('client_id', id)
      .maybeSingle();

    if (!hasAccess) {
      throw new Error('Access denied: You do not have permission to view this client');
    }
  }

  return data;
}

export async function getClientAppointments(clientId: string) {
  const { data, error } = await supabase
    .from('appointments')
    .select('*')
    .eq('client_id', clientId)
    .order('date', { ascending: true })
    .order('time', { ascending: true });

  if (error) throw error;
  return data;
}

export async function getClientProspectingDays(clientId: string) {
  const { data, error } = await supabase
    .from('prospecting_days')
    .select('*')
    .eq('client_id', clientId)
    .order('date', { ascending: false });

  if (error) throw error;
  return data;
}

export async function getProspectingDays(clientIds?: string | string[], options?: { limit?: number; offset?: number }) {
  let query = supabase
    .from('prospecting_days')
    .select(`
      *
    `)
    // TODO: Re-enable client join once RLS policies are fixed
    // clients (
    //   id,
    //   name
    // )
    .order('date', { ascending: false });

  if (clientIds) {
    if (Array.isArray(clientIds)) {
      if (clientIds.length > 0) {
        query = query.in('client_id', clientIds);
      } else {
        return [];
      }
    } else {
      query = query.eq('client_id', clientIds);
    }
  }

  if (options?.limit) {
    query.limit(options.limit);
  }

  if (options?.offset) {
    query = query.range(options.offset, options.offset + (options.limit || 20) - 1);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data;
}

export async function createUser(userData: {
  email: string;
  password: string;
  full_name: string;
  role: 'admin' | 'client';
  client_id?: string;
}) {
  const { data, error } = await supabase.rpc('create_user_with_role', {
    email: userData.email,
    password: userData.password,
    full_name: userData.full_name,
    user_role: userData.role,
    client_id: userData.client_id
  });
  
  if (error) throw error;
  return data;
}

export async function getUsers() {
  const { data, error } = await supabase.rpc('list_users');
  if (error) throw error;
  return data;
}

// Add count functions for pagination
export async function getAppointmentsCount(clientIds?: string | string[]) {
  let query = supabase
    .from('appointments')
    .select('*', { count: 'exact', head: true });

  if (clientIds) {
    if (Array.isArray(clientIds)) {
      if (clientIds.length > 0) {
        query = query.in('client_id', clientIds);
      } else {
        return 0;
      }
    } else {
      query = query.eq('client_id', clientIds);
    }
  }

  const { count, error } = await query;
  if (error) throw error;
  return count || 0;
}

export async function getClientsCount() {
  const { count, error } = await supabase
    .from('clients')
    .select('*', { count: 'exact', head: true });

  if (error) throw error;
  return count || 0;
}

export async function getProspectingDaysCount(clientIds?: string | string[]) {
  let query = supabase
    .from('prospecting_days')
    .select('*', { count: 'exact', head: true });

  if (clientIds) {
    if (Array.isArray(clientIds)) {
      if (clientIds.length > 0) {
        query = query.in('client_id', clientIds);
      } else {
        return 0;
      }
    } else {
      query = query.eq('client_id', clientIds);
    }
  }

  const { count, error } = await query;
  if (error) throw error;
  return count || 0;
}

// Add function to get appointment statistics from database
export async function getAppointmentStats() {
  const { data, error } = await supabase
    .from('appointments')
    .select('status');

  if (error) throw error;

  const stats = {
    total: data.length,
    effectue: data.filter(a => a.status === 'Completed' || a.status === 'effectué').length,
    planifie: data.filter(a => a.status === 'Scheduled' || a.status === 'planifié').length,
    confirme: data.filter(a => a.status === 'Confirmed' || a.status === 'confirmé').length,
    annule: data.filter(a => a.status === 'Cancelled' || a.status === 'annulé').length,
    reporte: data.filter(a => a.status === 'Postponed' || a.status === 'reporté').length,
  };

  return stats;
}

export async function createClient(clientData: {
  name: string;
  email: string;
  phone: string;
  company_name: string;
}) {
  const { data, error } = await supabase
    .from('clients')
    .insert([{
      name: clientData.name.trim(),
      email: clientData.email.trim().toLowerCase(),
      phone: clientData.phone.trim(),
      company_name: clientData.company_name.trim(),
      statut: 'actif',
    }])
    .select()
    .maybeSingle();

  if (error) throw error;
  if (!data) throw new Error('Failed to create client');
  return data;
}

export async function createAppointment(appointmentData: {
  title: string;
  date: string;
  time: string;
  prospect_name: string;
  prospect_linkedin?: string;
  job_title?: string;
  company_name: string;
  company_linkedin?: string;
  phone?: string;
  description?: string;
  location?: string;
  notes?: string;
  client_id: string;
  status?: string;
}) {
  const { data, error } = await supabase
    .from('appointments')
    .insert([{
      ...appointmentData,
      booking_date: new Date().toISOString()
    }])
    .select(`
      *,
      clients (
        id,
        name
      )
    `)
    .maybeSingle();

  if (error) throw error;
  if (!data) throw new Error('Failed to create appointment');
  return data;
}

export async function getAppointmentsByProspectingDate(clientId: string, prospectingDate: string, prospectingEmail?: string | null) {
  const startOfDay = new Date(prospectingDate);
  startOfDay.setHours(0, 0, 0, 0);

  const endOfDay = new Date(prospectingDate);
  endOfDay.setHours(23, 59, 59, 999);

  let query = supabase
    .from('appointments')
    .select(`
      id,
      title,
      status,
      date,
      time,
      prospect_name,
      job_title,
      company_name,
      booking_date,
      email_sales_hook,
      created_at
    `)
    .eq('client_id', clientId)
    .gte('created_at', startOfDay.toISOString())
    .lte('created_at', endOfDay.toISOString());

  if (prospectingEmail) {
    query = query.eq('email_sales_hook', prospectingEmail);
  }

  query = query.order('created_at', { ascending: true });

  const { data, error } = await query;

  if (error) throw error;
  return data || [];
}

export async function getSdrs() {
  const { data, error } = await supabase
    .from('profiles')
    .select(`
      user_id,
      sales_name,
      email,
      role,
      squad_id,
      squads ( name, color )
    `)
    .in('role', ['admin', 'super_admin', 'sdr'])
    .order('sales_name');

  if (error) throw error;

  const profiles = data || [];

  return profiles.sort((a, b) => {
    if (a.squad_id && !b.squad_id) return -1;
    if (!a.squad_id && b.squad_id) return 1;
    return (a.sales_name || '').localeCompare(b.sales_name || '');
  });
}

export async function getActiveSDRs(date?: Date | string) {
  let targetDate: string;

  if (date instanceof Date) {
    targetDate = date.toISOString().slice(0, 10);
  } else if (typeof date === 'string') {
    targetDate = date;
  } else {
    targetDate = new Date().toISOString().slice(0, 10);
  }

  const { data, error } = await supabase
    .from("prospecting_schedule")
    .select(`
      sdr_id,
      profiles:sdr_id (
        sales_name
      ),
      client_id,
      clients:client_id (
        name
      )
    `)
    .eq("date", targetDate)
    .eq("status", "planned")
    .eq("day_type", "prospection")
    .not("sdr_id", "is", null);

  if (error) {
    console.error("Error fetching active SDRs:", error);
    return [];
  }

  return data.map((row) => ({
    sdr_id: row.sdr_id,
    sdr_name: row.profiles?.sales_name || "Inconnu",
    client_id: row.client_id,
    client_name: row.clients?.name || "Sans client"
  }));
}

export async function getSquads() {
  const { data, error } = await supabase
    .from('squads')
    .select('id, name, color')
    .order('name');

  if (error) throw error;
  return data || [];
}

export async function getUsersWithClientInfo() {
  const { data: profiles, error } = await supabase
    .from('profiles')
    .select('user_id, role, email, sales_name')
    .order('email');

  if (error) throw error;
  if (!profiles) return [];

  const usersWithClients = await Promise.all(
    profiles.map(async (profile) => {
      const { data: clientLinks } = await supabase
        .from('profile_clients')
        .select(`
          client_id,
          clients (
            id,
            name,
            company_name
          )
        `)
        .eq('profile_id', profile.user_id);

      return {
        ...profile,
        client_associations: clientLinks || [],
        clients: clientLinks && clientLinks.length > 0 ? clientLinks[0].clients : null,
        client_id: clientLinks && clientLinks.length > 0 ? clientLinks[0].client_id : null
      };
    })
  );

  return usersWithClients;
}

export async function updateUserClientAssociation(userId: string, clientId: string) {
  const { error } = await supabase
    .from('profile_clients')
    .insert({ profile_id: userId, client_id: clientId });

  if (error) throw error;

  const { data: profile } = await supabase
    .from('profiles')
    .select('user_id, role, email, sales_name')
    .eq('user_id', userId)
    .maybeSingle();

  const { data: clientLinks } = await supabase
    .from('profile_clients')
    .select(`
      client_id,
      clients (
        id,
        name,
        company_name
      )
    `)
    .eq('profile_id', userId);

  return {
    ...profile,
    client_associations: clientLinks || [],
    clients: clientLinks && clientLinks.length > 0 ? clientLinks[0].clients : null,
    client_id: clientLinks && clientLinks.length > 0 ? clientLinks[0].client_id : null
  };
}

export async function removeUserClientAssociation(userId: string, clientId: string) {
  const { error } = await supabase
    .from('profile_clients')
    .delete()
    .eq('profile_id', userId)
    .eq('client_id', clientId);

  if (error) throw error;

  const { data: profile } = await supabase
    .from('profiles')
    .select('user_id, role, email, sales_name')
    .eq('user_id', userId)
    .maybeSingle();

  const { data: clientLinks } = await supabase
    .from('profile_clients')
    .select(`
      client_id,
      clients (
        id,
        name,
        company_name
      )
    `)
    .eq('profile_id', userId);

  return {
    ...profile,
    client_associations: clientLinks || [],
    clients: clientLinks && clientLinks.length > 0 ? clientLinks[0].clients : null,
    client_id: clientLinks && clientLinks.length > 0 ? clientLinks[0].client_id : null
  };
}

export async function getUnassociatedUsers() {
  const { data, error } = await supabase
    .from('profiles')
    .select(`
      user_id,
      role,
      email,
      sales_name,
      client_id
    `)
    .is('client_id', null)
    .order('email');

  if (error) throw error;
  return data || [];
}

export async function updateAppointmentStatus(appointmentId: string, status: string) {
  const { data, error } = await supabase
    .from('appointments')
    .update({ status })
    .eq('id', appointmentId)
    .select(`
      id, title, status, date, time, phone, location, description, notes,
      prospect_name, prospect_linkedin,
      company_name, company_linkedin,
      call_id, recording_path, job_title, client_id,
      clients ( id, name )
    `)
    .maybeSingle();

  if (error) throw error;
  if (!data) throw new Error('Failed to update appointment status');
  return data;
}

export async function updateAppointmentDateTimeAndStatus(
  appointmentId: string,
  date: string,
  time: string,
  status: string
) {
  const { data, error } = await supabase
    .from('appointments')
    .update({ date, time, status })
    .eq('id', appointmentId)
    .select(`
      id, title, status, date, time, phone, location, description, notes,
      prospect_name, prospect_linkedin,
      company_name, company_linkedin,
      call_id, recording_path, job_title, client_id,
      clients ( id, name )
    `)
    .maybeSingle();

  if (error) throw error;
  if (!data) throw new Error('Failed to update appointment');
  return data;
}